import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { ErrorAlert } from '@/components/Alert';
import Button, { BUTTON_KIND } from '@/components/Button';
import Input from '@/components/Input';
import LayoutStandard from '@/components/LayoutStandard';
import { AuthFlowSession } from '@/helpers/sessionUtils';
import useAuth from '@/hooks/useAuth';
import usePageHeight from '@/hooks/usePageHeight';

function AuthLinkAccountPage() {
  const {
    linkSocialAccount,
    loading,
    setAuthError,
    authError,
    user,
    redirectToShopify,
  } = useAuth();
  const pageHeight = usePageHeight();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const emailToLink = searchParams.get('email');
  const accessToken = searchParams.get('access_token');
  const isShopify = AuthFlowSession.isShopify();

  // Redirect if user is already logged in
  if (user) {
    // Clean up sessionStorage and redirect appropriately
    AuthFlowSession.clear();
    if (isShopify) {
      redirectToShopify();
      return null;
    }
    return <Navigate to="/" replace />;
  }

  // If params are missing, redirect to appropriate login page
  if (!emailToLink || !accessToken) {
    AuthFlowSession.clear();
    const loginPath = isShopify ? '/shopify/login' : '/login';
    return <Navigate to={loginPath} replace />;
  }

  const handleLinkAccount = async () => {
    setError(null);
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (!emailToLink || !accessToken) {
      setError(
        'Required information is missing from URL. Cannot link account.',
      );
      return;
    }
    linkSocialAccount(password, accessToken);
  };

  useEffect(() => {
    if (authError) {
      setError('Failed to link account. Please try again.');
    }
  }, [authError]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleLinkAccount();
    }
  };

  return (
    <LayoutStandard heading="Link your account" backgroundColor="bg-gray-50">
      <div
        className="sm:mx-auto sm:w-full sm:max-w-md lg:max-w-lg"
        style={{ maxHeight: pageHeight }}
      >
        <div className="bg-white py-2 px-4 sm:rounded-lg sm:px-10 sm:py-8 lg:border lg:border-gray-100">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              An account already exists with the email{' '}
              <span className="font-medium">{emailToLink}</span>. <br />
              Please enter your password to link your social login.
            </p>

            {error && <ErrorAlert message={error} />}

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              onKeyDown={handleKeyPress}
            />

            <div className="flex flex-col gap-2">
              <Button
                buttonText="Link Account"
                onClick={handleLinkAccount}
                fullWidth
                loading={loading}
              />
              <Button
                kind={BUTTON_KIND.WHITE}
                buttonText="Cancel"
                onClick={() => {
                  setAuthError(false);
                  AuthFlowSession.clear();
                  const loginPath = isShopify ? '/shopify/login' : '/login';
                  navigate(loginPath);
                }}
                fullWidth
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </LayoutStandard>
  );
}

export default AuthLinkAccountPage;
